// USEtox engine result table — multi-substance toxicity output.
//
// Today's payload (from `inferno/engines/usetox.py::query_usetox_for_emissions`):
//   {
//     human_cancer_cases, human_noncancer_cases,
//     freshwater_paf_m3_day, marine_paf_m3_day, terrestrial_paf_m3_day,
//   }
// (flat totals across all matched substances; the substance-level
// breakdown isn't persisted yet.)
//
// When the engine writes a `substances` array (Track C9 follow-on), the
// per-substance table renders with CAS, name, and per-endpoint values.

import React from "react";
import { toSigFigs } from "../../utils/format";

interface UsetoxEngineTableProps {
  payload: Record<string, any>;
}

interface SummaryStat {
  label: string;
  value: number;
  unit: string;
}

export function UsetoxEngineTable({ payload }: UsetoxEngineTableProps) {
  const substances = Array.isArray(payload?.substances) ? payload.substances : null;

  const stats: SummaryStat[] = [
    { label: "Human cancer", key: "human_cancer_cases", unit: "cases / yr" },
    { label: "Human non-cancer", key: "human_noncancer_cases", unit: "cases / yr" },
    { label: "Freshwater ecotox", key: "freshwater_paf_m3_day", unit: "PAF·m³·day" },
    { label: "Marine ecotox", key: "marine_paf_m3_day", unit: "PAF·m³·day" },
    { label: "Terrestrial ecotox", key: "terrestrial_paf_m3_day", unit: "PAF·m³·day" },
  ]
    .map((s) => ({
      label: s.label,
      value: typeof payload?.[s.key] === "number" ? payload[s.key] : NaN,
      unit: s.unit,
    }))
    .filter((s) => Number.isFinite(s.value));

  // Some payloads call them `cancer_cases` / `noncancer_cases` /
  // `freshwater_ecotox_pdf` — surface those if present too.
  const aliases: SummaryStat[] = [
    { label: "Cancer cases", key: "cancer_cases", unit: "cases / yr" },
    { label: "Non-cancer cases", key: "noncancer_cases", unit: "cases / yr" },
    { label: "Freshwater ecotox PDF", key: "freshwater_ecotox_pdf", unit: "PDF" },
  ]
    .map((s) => ({
      label: s.label,
      value: typeof payload?.[s.key] === "number" ? payload[s.key] : NaN,
      unit: s.unit,
    }))
    .filter((s) => Number.isFinite(s.value));

  const allStats = [...stats, ...aliases];

  if (allStats.length === 0 && !substances) {
    return (
      <div className="space-y-2 text-sm text-text-muted">
        <p>
          USEtox v2.14 has CFs for ~28 inorganics (Hg, Pb, Cd, As, Cr, Ni, …) and
          ~3,000 organics. The engine ran but the per-plant species masses for
          these substances aren&rsquo;t populated by the current reconciler — that
          ingest path (Phase 0f) is still pending.
        </p>
        <p>
          Once populated, this panel will show avoided cancer cases, non-cancer
          cases, and freshwater / marine / terrestrial ecotox PAF·m³·day, summed
          across the displaced fleet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {allStats.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          {allStats.map((s) => (
            <div key={s.label} className="bg-surface-800 rounded p-3">
              <div className="text-xs text-text-muted uppercase tracking-wider">{s.label}</div>
              <div className="text-base text-text-primary mt-1">{toSigFigs(s.value)}</div>
              <div className="text-xs text-text-muted mt-0.5">{s.unit}</div>
            </div>
          ))}
        </div>
      )}

      {substances && substances.length > 0 && (
        <div>
          <h5 className="text-sm font-medium text-text-primary mb-2">Top substances</h5>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-600">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                    Substance
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                    CAS
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                    Cancer cases
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                    Non-cancer cases
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                    Freshwater ecotox PDF
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-700 divide-y divide-border">
                {substances.slice(0, 25).map((s: any, idx: number) => (
                  <tr key={s?.cas ?? idx} className="hover:bg-surface-600 transition-colors">
                    <td className="px-4 py-2 text-sm font-medium text-text-primary">
                      {s?.name ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-sm text-text-secondary">{s?.cas ?? "—"}</td>
                    <td className="px-4 py-2 text-right text-sm text-text-secondary">
                      {typeof s?.cancer_cases === "number" ? toSigFigs(s.cancer_cases) : "—"}
                    </td>
                    <td className="px-4 py-2 text-right text-sm text-text-secondary">
                      {typeof s?.noncancer_cases === "number" ? toSigFigs(s.noncancer_cases) : "—"}
                    </td>
                    <td className="px-4 py-2 text-right text-sm text-text-secondary">
                      {typeof s?.freshwater_ecotox_pdf === "number"
                        ? toSigFigs(s.freshwater_ecotox_pdf)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
